import { motion } from "framer-motion";
import { Award, BadgeCheck, CheckCircle2, ShieldCheck, Star, TrendingUp } from "lucide-react";
import { REPUTATION, PROFILE, shortIdr } from "../data/kabelul";

const Stars = ({ n }) => (
  <span className="k-stars">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={13} className={i <= n ? "on" : ""} />
    ))}
  </span>
);

export default function ReputationPage({ setPage }) {
  const maxJobs = Math.max(...REPUTATION.timeline.map((t) => t.jobs));

  return (
    <div className="page-inner">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="page-intro">
        <div>
          <p className="eyebrow">REPUTATION · TRUST BUILT ON DELIVERED WORK</p>
          <h1>Your track record</h1>
          <p className="subtitle">Every completed job feeds back into your Passport — Trust compounds over time.</p>
        </div>
      </motion.div>

      <section className="k-rep-hero" data-testid="reputation-hero">
        <div className="k-rep-score">
          <div className="k-rep-big">{REPUTATION.overall}</div>
          <Stars n={Math.round(REPUTATION.overall)} />
          <small>{REPUTATION.reviewCount} verified reviews</small>
        </div>
        <div className="k-rep-breakdown">
          {[5, 4, 3, 2, 1].map((r) => {
            const pct = (REPUTATION.breakdown[r] / REPUTATION.reviewCount) * 100 || 0;
            return (
              <div className="k-rep-bar" key={r}>
                <span>{r} ★</span>
                <div className="k-progress-bar"><div className="k-progress-fill" style={{ width: `${pct}%` }} /></div>
                <em>{REPUTATION.breakdown[r]}</em>
              </div>
            );
          })}
        </div>
        <div className="k-rep-metrics">
          {REPUTATION.metrics.map((m) => (
            <div key={m.label}>
              <small>{m.label.toUpperCase()}</small>
              <b>{m.value}%</b>
              <small className="note">{m.note}</small>
            </div>
          ))}
        </div>
      </section>

      <section className="section-block">
        <div className="section-title">
          <div>
            <div className="section-kicker">EARNINGS & ACTIVITY · LAST 6 MONTHS</div>
            <h2>Consistent, growing</h2>
          </div>
        </div>
        <div className="k-timeline">
          {REPUTATION.timeline.slice().reverse().map((t, i) => (
            <div className="k-timeline-col" key={t.period} data-testid={`timeline-col-${i}`}>
              <div className="k-bar-track">
                <div className="k-bar-fill" style={{ height: `${(t.jobs / maxJobs) * 100}%` }} />
              </div>
              <b>{t.jobs} jobs</b>
              <small>{shortIdr(t.earnings)}</small>
              <span>{t.period}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="section-block">
        <div className="section-title">
          <div>
            <div className="section-kicker">RECENT CLIENT REVIEWS</div>
            <h2>What clients say</h2>
          </div>
        </div>
        <div className="k-reviews-list">
          {REPUTATION.reviews.map((r, i) => (
            <div className="k-review-card" key={r.id} data-testid={`review-card-${i}`}>
              <div className="k-review-head">
                <div className={`k-review-avatar ${r.tone}`}>{r.clientInitials}</div>
                <div>
                  <b>{r.client}</b>
                  <small>{r.job}</small>
                </div>
                <div className="k-review-side">
                  <Stars n={r.rating} />
                  <small>{r.date}</small>
                </div>
              </div>
              <p>{r.comment}</p>
              <div className="k-badge-row">
                {r.badges.map((b) => (
                  <span key={b} className="k-badge-soft"><CheckCircle2 size={11} /> {b}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="k-eco-note">
        <ShieldCheck size={16} />
        <p>Reputation is tamper-evident. Reviews only appear once a KABELUL job is marked complete by the client. <b>Trust that scales.</b></p>
      </section>
    </div>
  );
}
